import React, { useState } from 'react';
// UI
import { Box, IconButton, TextField, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import noteDeleteButtonStyle from './style';
// Types
import { ChannelType } from '@trii/types/dist/Common/Channels';
import { NewAddress } from '../../../../../../../utils/contactHelper';
import { IContactAddress } from '@trii/types/dist/Contacts';
import { useEditContactApi } from '../../../../../../../context/EditContactApiContext';
import { useEditEntity } from '../../../../../../../context/EditEntityContext';
import OptionsMenu from '../../components/Phones/components/Body/components/Phone/components/OptionsMenu';
import { useTranslationContext } from '../../../../../../../context/TranslationContext';

type Props = {
  addressData: NewAddress | IContactAddress;
  handleNoteFieldChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    addressId: string,
    addressType: ChannelType,
  ) => void;
  handleDeleteAddress: (addressId: string, addressType: ChannelType) => void;
  checked?: boolean;
  entityId?: string;
  mode?: string;
  setWarnings?: (warnings: string[]) => void;
  isDeletingAddress?: string | false;
  replaceAddress?: (addressId: string, newAddress: IContactAddress) => void;
  addWhatsappVerified?: (newAddress: IContactAddress) => void;
};

const NoteDeleteButton = (props: Props) => {
  const t = useTranslationContext();
  const api = useEditContactApi();
  const { editEntity, entityId: entityIdFromContext } = useEditEntity();

  const [isSaving, setIsSaving] = useState(false);

  const checked = props.checked ?? true;
  const entityId = props.entityId ?? entityIdFromContext;

  const { note, channelType, id, address } = props.addressData;
  const addressIsSaved = !props.addressData.hasOwnProperty('isSaved');

  const replaceAddress = props.replaceAddress ?? editEntity.action.replaceAddress;
  const addWhatsappVerified =
    props.addWhatsappVerified ||
    (typeof (editEntity.action as any)?.addWhatsappVerified === 'function'
      ? (editEntity.action as any).addWhatsappVerified
      : undefined);

  const handleSetAddress = async (type: ChannelType) => {
    if (!api) return null;
    if (!entityId) return null;

    const newAddress = await api.setContactAddress({
      data: {
        address,
        contactId: entityId,
        type,
      },
    });

    replaceAddress(id, newAddress);
    return newAddress;
  };

  const handleUpdateAddress = async () => {
    if (!api) return;
    if (!entityId) return;

    const updated = await api.updateContactAddress({
      data: {
        contactAddressId: id,
        contactId: entityId,
        note: note ?? '',
      },
    });

    replaceAddress(id, updated);
  };

  const handleSave = async () => {
    setIsSaving(true);

    if (addressIsSaved) {
      await handleUpdateAddress();
      setIsSaving(false);
    } else {
      const newAddress = await handleSetAddress(channelType);
      if (newAddress) setIsSaving(false);
    }
  };

  const handleLinkAccountSave = async () => {
    setIsSaving(true);
    const newAddress = await handleSetAddress(13 as unknown as ChannelType);
    if (newAddress && addWhatsappVerified) {
      addWhatsappVerified(newAddress);
    }
    if (newAddress) setIsSaving(false);
  };

  return (
    <Box
      sx={{
        ...noteDeleteButtonStyle.container,
      }}
    >
      {addressIsSaved && (
        <TextField
          size="small"
          label={t('global.note')}
          variant="outlined"
          onChange={(e) => props.handleNoteFieldChange(e, id, channelType)}
          value={note}
          fullWidth
          InputLabelProps={{
            shrink: true,
          }}
          className="smallInput"
          sx={noteDeleteButtonStyle.noteTextField}
        />
      )}
      <IconButton onClick={handleSave}>
        {isSaving ? <CircularProgress size={24} /> : <SaveIcon color="primary" />}
      </IconButton>
      <OptionsMenu
        onDelete={() => props.handleDeleteAddress(id, channelType)}
        addressData={props.addressData as any}
        handleLinkAccountSave={handleLinkAccountSave}
        checked={checked && !isSaving} // Pass the checked prop to OptionsMenu
        onCheckContactAddress={async (type) => {
          if (!api) return null;
          try {
            return await api.checkContactAddress({
              data: {
                address,
                type,
              },
            });
          } catch {
            return null;
          }
        }}
      />
    </Box>
  );
};

export default NoteDeleteButton;
